import React, { FC } from 'react'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material'

interface DisableViewModalProps {
  open: boolean
  viewLabel: string
  isSaving?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * VERA-299 — confirm before turning a share link off.
 *
 * Worth confirming because disabling is not cosmetic: the server stops serving the
 * view immediately, so anyone holding the link loses access mid-session.
 *
 * Follows the app's existing confirm-dialog shape (CertificatePage's status-change
 * dialog): stacked full-width buttons, primary on top.
 */
export const DisableViewModal: FC<DisableViewModalProps> = ({
  open,
  viewLabel,
  isSaving,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme()

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', p: 0 } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 400, pb: 1, textTransform: 'none' }}>
        Disable view
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Copy per the design. Kept as separate sentences rather than em-dash-joined
            clauses, per the UI copy convention, with roomier line-height. */}
        <Typography
          sx={{ fontSize: 16, fontWeight: 400, color: theme.palette.text.secondary, lineHeight: 1.5 }}
        >
          Are you sure you want to disable {viewLabel}? Users with access will lose it
          immediately, and they won&apos;t be able to view this certificate.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
        <Button
          fullWidth
          variant='contained'
          disabled={isSaving}
          onClick={onConfirm}
          sx={{
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
            borderRadius: '16px',
            textTransform: 'none',
            py: 1.5,
            '&:hover': { backgroundColor: theme.palette.grey[800] },
          }}
        >
          Disable
        </Button>
        <Button
          fullWidth
          variant='outlined'
          disabled={isSaving}
          onClick={onCancel}
          sx={{
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.common.white,
            borderRadius: '16px',
            textTransform: 'none',
            py: 1.5,
            ml: '0 !important',
            '&:hover': { backgroundColor: theme.palette.grey[50] },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
